'use client'

import { useBlockCloseWindow } from '@/core/guards'
import { cn } from '@/core/lib/utils/cn'
import { Alert } from '@/core/ui/atoms/alert'
import { Button } from '@/core/ui/atoms/button'
import { Checkbox } from '@/core/ui/forms/checkbox'
import { FormNumberInput } from '@/core/ui/forms/number-input'
import { Users } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { BookFormModel } from '../../types/models/book-form.model'
import { DateTimeInput } from './date-time-input'
import { TripLocationInput } from './trip-location-input'
import { useBookForm } from './use-book-form'

type Props = {
  className?: string
  title?: React.ReactNode
  initialValues?: BookFormModel
  nextStep?: string
  id?: string
  onSuccess?: () => void
} & (
  | {
      actions?: {
        submit: (isLoading: boolean) => React.ReactNode
        cancel?: (isLoading: boolean) => React.ReactNode
      }
      submitText?: never
    }
  | {
      actions?: never
      submitText?: React.ReactNode
    }
)
export const BookForm: React.FC<Props> = ({
  title,
  className,
  actions,
  submitText,
  initialValues,
  nextStep,
  id,
  onSuccess,
}) => {
  const t = useTranslations()
  const [needChildSeats, setNeedChildSeats] = useState(false)
  const { error, isPending, form, onSubmit } = useBookForm({
    initialValues,
    nextStep,
    onSuccess,
  })
  useBlockCloseWindow({
    hasUnsavedChanges: Object.values(form.formState.dirtyFields).length > 0,
  })
  const hasChildSeats = Boolean(
    form.watch('carrier') || form.watch('booster') || form.watch('childSeats'),
  )
  const childSeatFields = [
    {
      label: t('forms.carrier'),
      description: `0${t('forms.month')} - 9${t('forms.month')}`,
      name: 'carrier,',
    },
    {
      label: t('forms.childSeats'),
      description: `9${t('forms.month')} - 9${t('forms.year')}`,
      name: 'childSeats,',
    },
    {
      label: t('forms.booster'),
      description: `3${t('forms.year')} - 12${t('forms.year')}`,
      name: 'booster,',
    },
  ]

  return (
    <FormProvider {...form}>
      <form
        id={id}
        onSubmit={onSubmit}
        className={cn(
          'flex w-full flex-col gap-3 rounded-md bg-white p-6 font-medium md:max-w-md',
          className,
        )}
        autoComplete='off'
      >
        <div className='text-navy text-center text-xl min-[28rem]:text-start'>
          {title}
        </div>
        <TripLocationInput initialValues={initialValues} />
        <DateTimeInput initialValues={initialValues} />
        <div className='flex flex-col items-start justify-between gap-3 min-[28rem]:flex-row'>
          <FormNumberInput
            control={form.control}
            label={t('forms.passengers')}
            name='passengers'
            className='w-full'
          />
          <FormNumberInput
            control={form.control}
            label={t('forms.checkedLuggage')}
            name='checkedLuggage'
            className='w-full'
          />
        </div>
        <div className='flex flex-col items-start justify-between gap-3 min-[28rem]:flex-row'>
          <FormNumberInput
            control={form.control}
            label={t('forms.handLuggage')}
            name='handLuggage'
            className='w-full flex-1'
          />
          <div className='hidden flex-0 sm:block sm:flex-1'>&nbsp;</div>
        </div>
        <div className='bg-pale-blue-alt border-sky-blue text-dark-teal flex items-center gap-2 rounded-lg border p-2'>
          <Users className='bg-sky-blue h-7 w-7 rounded-full p-2 text-white' />
          <div>
            <p className='text-lg font-medium capitalize'>
              Meet & greet included
            </p>
            <p className='text-sm font-light'>1 hour free waiting time</p>
          </div>
        </div>
        <Checkbox
          color='danger'
          radius='sm'
          isSelected={needChildSeats}
          onValueChange={setNeedChildSeats}
          classNames={{
            label: 'text-grey',
            icon: 'text-white',
          }}
          defaultSelected={hasChildSeats}
        >
          {t('forms.addChildSeats')}
        </Checkbox>

        {needChildSeats && (
          <div className='flex flex-col items-start justify-between gap-3 sm:flex-row'>
            {childSeatFields.map(field => (
              <FormNumberInput
                key={field.name}
                control={form.control}
                label={field.label}
                name={field.name}
                description={field.description}
                className='flex-1'
              />
            ))}
          </div>
        )}

        <Alert error={error} />
        <div className='flex flex-col items-stretch gap-3 min-[28rem]:flex-row min-[28rem]:items-center'>
          {actions?.cancel && (
            <span className='flex-1'>{actions?.cancel(isPending)}</span>
          )}
          <span className='flex-1'>
            {actions?.submit(isPending) ?? (
              <Button
                type='submit'
                loading={isPending}
                className='bg-navy h-12 w-full rounded-md px-5 py-3 text-white'
              >
                {submitText || t('actions.bookRide')}
              </Button>
            )}
          </span>
        </div>
      </form>
    </FormProvider>
  )
}
